/**
 * OpenRouter Provider
 *
 * Cloud LLM provider using OpenRouter API (OpenAI-compatible)
 * Provides access to multiple models through a single API
 * Default: meta-llama/llama-3.1-8b-instruct:free
 */

import { LLMProvider, GenerateOptions } from './base';

export class OpenRouterProvider implements LLMProvider {
  name = 'OpenRouter';

  constructor(
    private apiKey: string,
    private model: string = 'meta-llama/llama-3.1-8b-instruct:free'
  ) {}

  async generateInsights(prompt: string, options?: GenerateOptions): Promise<string> {
    const timeout = options?.timeout || 30000; // 30 seconds default
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.apiKey}`,
          'HTTP-Referer':
            typeof window !== 'undefined' ? window.location.origin : 'https://valiyou.com',
          'X-Title': 'Valiyou - AI Insights',
        },
        body: JSON.stringify({
          model: this.model,
          messages: [
            {
              role: 'system',
              content: 'You are a sports business analyst that provides insights in JSON format.',
            },
            {
              role: 'user',
              content: prompt,
            },
          ],
          temperature: options?.temperature || 0.7,
          max_tokens: options?.maxTokens || 1000,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`OpenRouter API error: ${error.error?.message || response.statusText}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`OpenRouter request timed out after ${timeout}ms`);
      }

      throw new Error(
        `OpenRouter error: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async isAvailable(): Promise<boolean> {
    if (!this.apiKey) {
      return false;
    }

    try {
      // Test with a simple models list request
      const response = await fetch('https://openrouter.ai/api/v1/models', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.apiKey}`,
        },
        signal: AbortSignal.timeout(3000),
      });

      return response.ok;
    } catch {
      return false;
    }
  }
}
